import { useEffect } from 'react';

interface SeoSettings {
  siteTitle?: string;
  description?: string;
  keywords?: string;
  canonicalOrigin?: string;
  organizationName?: string;
  socialImage?: string;
  baiduVerification?: string;
  googleVerification?: string;
  bingVerification?: string;
}

function setMeta(key: string, value: string, property = false) {
  const selector = property ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!value) { element?.remove(); return; }
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(property ? 'property' : 'name', key);
    document.head.append(element);
  }
  element.content = value;
}

export function SeoManager({ screen, operationSlug }: { screen: string; operationSlug?: string }) {
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/site-settings', { signal: controller.signal, cache: 'no-store' })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        const seo = (data?.settings?.seo || {}) as SeoSettings;
        const frontend = data?.settings?.frontend || {};
        const siteName = frontend.siteName || seo.organizationName || '伊小满拼豆';
        const labels: Record<string, string> = { gallery: '图纸广场', workspace: '绘制工具', palettes: '色卡中心', tutorials: '拼豆教程', products: '合作商品', operation: operationSlug || '专题页面' };
        document.title = screen === 'gallery' ? (seo.siteTitle || `${siteName} - 拼豆图纸、色卡与创作工具`) : `${labels[screen] || siteName} - ${siteName}`;
        setMeta('description', seo.description || '发现拼豆图纸，查询拼豆色卡与教程，使用图片转图纸和逐色施工工具。');
        setMeta('keywords', seo.keywords || '拼豆,拼豆图纸,拼豆色卡,拼豆教程');
        setMeta('og:title', document.title, true);
        setMeta('og:description', seo.description || '', true);
        setMeta('og:image', seo.socialImage || `${window.location.origin}/logo.png`, true);
        setMeta('og:type', screen === 'operation' ? 'article' : 'website', true);
        setMeta('og:locale', 'zh_CN', true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', document.title);
        setMeta('twitter:description', seo.description || '');
        setMeta('twitter:image', seo.socialImage || `${window.location.origin}/logo.png`);
        setMeta('baidu-site-verification', seo.baiduVerification || '');
        setMeta('google-site-verification', seo.googleVerification || '');
        setMeta('msvalidate.01', seo.bingVerification || '');
        let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.append(canonical); }
        const channelPaths: Record<string, string> = { gallery: 'guangchang', palettes: 'seka', tutorials: 'jiaocheng', products: 'hezuo-shangpin' };
        canonical.href = `${seo.canonicalOrigin || window.location.origin}/${screen === 'operation' && operationSlug ? `zhuanti/${encodeURIComponent(operationSlug)}` : channelPaths[screen] || ''}`;
        let structured = document.head.querySelector<HTMLScriptElement>('script[data-site-organization]');
        if (!structured) { structured = document.createElement('script'); structured.type = 'application/ld+json'; structured.dataset.siteOrganization = 'true'; document.head.append(structured); }
        structured.textContent = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Organization', name: seo.organizationName || siteName, url: seo.canonicalOrigin || window.location.origin, logo: `${seo.canonicalOrigin || window.location.origin}/logo.png` });
      }).catch(() => undefined);
    return () => controller.abort();
  }, [operationSlug, screen]);
  return null;
}
